import {gql} from '@apollo/client';
import {PodExemptDictItemType, PodExemptPresignType, PodExemptReportType} from './types';
import {POD_EXEMPT_REPORT_CORE_FIELDS} from './queries';

// ---------- DSP 端 ----------

export const CREATE_POD_EXEMPT_REPORT = gql`
    mutation CreatePodExemptReport($input: CreatePodExemptReportInput!) {
        createPodExemptReport(input: $input) {
            ${POD_EXEMPT_REPORT_CORE_FIELDS}
        }
    }
`;

export interface CreatePodExemptReportResponse {
  createPodExemptReport: PodExemptReportType;
}

export const CLOSE_POD_EXEMPT_REPORT = gql`
    mutation ClosePodExemptReport($reportId: Long!) {
        closePodExemptReport(reportId: $reportId) {
            ${POD_EXEMPT_REPORT_CORE_FIELDS}
        }
    }
`;

export interface ClosePodExemptReportResponse {
  closePodExemptReport: PodExemptReportType;
}

export const OFFLINE_POD_EXEMPT_REPORT = gql`
    mutation OfflinePodExemptReport($reportId: Long!) {
        offlinePodExemptReport(reportId: $reportId) {
            ${POD_EXEMPT_REPORT_CORE_FIELDS}
        }
    }
`;

export interface OfflinePodExemptReportResponse {
  offlinePodExemptReport: PodExemptReportType;
}

/** 中台人工下线已生效单（数据域不限；MANUAL 语义不拦截重报，永久拦截只由抽查下线产生） */
export const QC_OFFLINE_POD_EXEMPT_REPORT = gql`
    mutation QcOfflinePodExemptReport($reportId: Long!) {
        qcOfflinePodExemptReport(reportId: $reportId) {
            ${POD_EXEMPT_REPORT_CORE_FIELDS}
        }
    }
`;

export interface QcOfflinePodExemptReportResponse {
  qcOfflinePodExemptReport: PodExemptReportType;
}

/**
 * 上传失败回报：只打日志 + metric，不落库，调用失败不影响用户流程。
 *
 * presign 之后的 PUT 是浏览器直连 R2、不经过 api-server，服务端对失败零感知，
 * 没有这个回报口就只能等用户截图。
 */
export const REPORT_POD_EXEMPT_UPLOAD_FAILURE = gql`
    mutation ReportPodExemptUploadFailure(
        $stage: String!
        $objectKey: String
        $errorName: String
        $httpStatus: Int
        $fileSizeBytes: Long
        $durationMs: Long
    ) {
        reportPodExemptUploadFailure(
            stage: $stage
            objectKey: $objectKey
            errorName: $errorName
            httpStatus: $httpStatus
            fileSizeBytes: $fileSizeBytes
            durationMs: $durationMs
        )
    }
`;

export const PRESIGN_POD_EXEMPT_MATERIAL = gql`
    mutation PresignPodExemptMaterial(
        $materialType: String!
        $fileName: String!
        $mimeType: String!
        $fileSize: Long
    ) {
        presignPodExemptMaterial(
            materialType: $materialType
            fileName: $fileName
            mimeType: $mimeType
            fileSize: $fileSize
        ) {
            uploadUrl
            objectKey
            expiresAt
        }
    }
`;

export interface PresignPodExemptMaterialResponse {
  presignPodExemptMaterial: PodExemptPresignType;
}

// ---------- 仓管端（初审） ----------

export const FIRST_REVIEW_POD_EXEMPT_REPORT = gql`
    mutation FirstReviewPodExemptReport($input: WmsPodExemptReviewInput!) {
        firstReviewPodExemptReport(input: $input) {
            ${POD_EXEMPT_REPORT_CORE_FIELDS}
        }
    }
`;

export interface FirstReviewPodExemptReportResponse {
  firstReviewPodExemptReport: PodExemptReportType;
}

// ---------- 审核端（终审 + 字典管理） ----------

export const FINAL_REVIEW_POD_EXEMPT_REPORT = gql`
    mutation FinalReviewPodExemptReport($input: PodExemptReviewInput!) {
        finalReviewPodExemptReport(input: $input) {
            ${POD_EXEMPT_REPORT_CORE_FIELDS}
        }
    }
`;

export interface FinalReviewPodExemptReportResponse {
  finalReviewPodExemptReport: PodExemptReportType;
}

export const SAVE_POD_EXEMPT_DICT_ITEM = gql`
    mutation SavePodExemptDictItem($input: SavePodExemptDictItemInput!) {
        savePodExemptDictItem(input: $input) {
            dictType
            code
            parentCode
            name
            nameZh
            nameEn
            nameEs
            extJson
            status
            sortNo
        }
    }
`;

export interface SavePodExemptDictItemInput {
  dictType: string;
  code: string;
  parentCode?: string | null;
  nameZh: string;
  nameEn?: string | null;
  nameEs?: string | null;
  extJson?: string | null;
  status?: number;
  sortNo?: number;
}

export interface SavePodExemptDictItemResponse {
  savePodExemptDictItem: PodExemptDictItemType;
}

export const DELETE_POD_EXEMPT_DICT_ITEM = gql`
    mutation DeletePodExemptDictItem($dictType: String!, $code: String!) {
        deletePodExemptDictItem(dictType: $dictType, code: $code)
    }
`;

export const DISABLE_POD_EXEMPT_DICT_ITEM = gql`
    mutation DisablePodExemptDictItem($dictType: String!, $code: String!) {
        disablePodExemptDictItem(dictType: $dictType, code: $code)
    }
`;

export interface DisablePodExemptDictItemResponse {
  disablePodExemptDictItem: boolean;
}
